/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import { BuilderUserContext } from '@gh/builder-shared';
import {
  ChangeStatusParams,
  SystemRoleBit,
  TodoRestService,
  UserAutocompleteDto,
  UserAutocompleteQuery,
  UserRestService,
} from '@gh/builder-shared-data';
import { ID } from '@gh/common-shared-data';
import { FilteredDataQuery, SlicedDataSource } from '@gh/core-data';
import { DialogRouteSnapshot } from '@gh/core-ui';
import { GridLayoutService } from '@gh/core-ui-grid';
import { map$ } from '@gh/rx/operators';

import { some, values } from 'lodash';

import { Observable } from 'rxjs';

import { TodoChangeStatusResponse, TodoDetails, TodoForm, TodoList, toForm } from './todo.model';

export interface TodoInputDialogConfig {
  todoId?: ID;
  selectedRows?: any[];
  activityTodo?: boolean;
}

@Injectable()
export class TodoListBusinessService {

  private userPrepopulatedMap: any;

  constructor(private todoRest: TodoRestService,
              private gridLayoutService: GridLayoutService,
              private userRest: UserRestService,
              private builderUserContext: BuilderUserContext) {
    this.userPrepopulatedMap = {
      master: this.builderUserContext.masterView && this.builderUserContext.isSwitched()
        && this.builderUserContext.hasSystemRole(SystemRoleBit.Corporate),
      officeSwitched: this.builderUserContext.officeView && this.builderUserContext.isSwitched(),
    };
  }

  initView(): any {
    return {
      gridLayoutInboxDone: this.gridLayoutService.get('todoInboxDone'),
      gridLayoutPassed: this.gridLayoutService.get('todoPassed'),
    };
  }

  initDialogView(route: DialogRouteSnapshot<TodoInputDialogConfig>): TodoForm | Observable<TodoForm> {
    return route.input.todoId ? this.getTodoForm(route.input.todoId) : this.prepareItemForCreate();
  }

  /**
   * Generates default values for any list To-do item
   * @returns {TodoListDto}
   */
  prepareListForCreate(): TodoList {
    return TodoRestService.prepareListForCreate();
  }

  /**
   * Generates default values for any To-do item
   * @returns {TodoFormDto}
   */
  prepareItemForCreate(): TodoForm {
    return TodoRestService.prepareItemForCreate();
  }

  /**
   * Change Status of list of To-do Resource action.
   * @param changeStatusParams
   */
  changeStatus(changeStatusParams: ChangeStatusParams): Observable<TodoChangeStatusResponse> {
    return this.todoRest.changeStatus(changeStatusParams);
  }

  /**
   * Creates new To-do Item
   * @param todo
   * @returns {Observable<TodoDetails>}
   */
  create(todo: TodoDetails): Observable<TodoDetails> {
    return this.todoRest.create(todo);
  }

  /**
   * Modifies To-do Item
   * @param todo
   * @returns {Observable<TodoDetails>}
   */
  update(todo: TodoDetails): Observable<TodoDetails> {
    return this.todoRest.update(todo);
  }

  /**
   * Removes list of To-do Items
   * @param todoList
   * @returns {Observable<TodoList[]>}
   */
  remove(todoList: TodoList[]): Observable<TodoList[]> {
    return this.todoRest.remove(todoList);
  }

  /**
   * Returns To-do Item according to passed ID
   * @param id
   * @returns {Observable<TodoDetails>}
   */
  get(id: ID): Observable<TodoDetails> {
    return this.todoRest.get(id);
  }

  /**
   * Returns To-do Item according to passed ID
   * @param id
   * @returns {Observable<TodoDetails>}
   */
  getTodoForm(id: ID): Observable<TodoDetails> {
    return this.todoRest.get(id).pipe(map$(toForm));
  }

  /**
   * Creates data source for list in Grid Component
   * @param filter
   * @returns {SlicedDataSource<TodoList>}
   */
  findAllAsDataSource(filter: any): SlicedDataSource<TodoList> {
    return this.todoRest.findAllAsDataSource(filter);
  }

  isUserPrepopulated(): boolean {
    const { userPrepopulatedMap } = this;
    return !some(values(userPrepopulatedMap), Boolean);
  }

  getAutocompleteUser(query: FilteredDataQuery<UserAutocompleteQuery>): Observable<UserAutocompleteDto[]> {
    return this.userRest.getAutocompleteUser(query);
  }
}
